import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	HostBinding,
	OnDestroy,
	OnInit,
	ViewChild,
	ViewEncapsulation,
} from "@angular/core";
import { FormGroupDirective, UntypedFormBuilder, UntypedFormGroup, Validators } from "@angular/forms";
import { select, Store } from "@ngrx/store";
import { AlertButtonColor } from "@obg/mat.ui.core";
import {
	CommonState,
	LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT,
	LogoutCooldownLimit,
	LogoutCooldownLimitAction,
	LogoutCooldownLimitSelector,
	LogoutCooldownLimitState,
} from "@obg/ngx.common";
import {
	AbstractTranslatableAlertService,
	AbstractTranslatableSnackBarService,
	Message,
	MessageService,
	MessageType,
} from "@obg/ngx.core";
import { TranslationGroup } from "@obg/ngx.core.content";
import { OBG_VALIDATORS } from "@obg/ngx.ui.common";
import { ignoreNil } from "@obg/rxjs";
import { merge, Subscription } from "rxjs";
import { take, tap } from "rxjs/operators";

import { ResponsibleGamingFormService } from "../../containers/responsible-gaming-form.service";
import { TRANSLATION_PREFIX } from "../logout-cooldown.const";

import { ZhhLogoutCooldownSubmitType } from "./zhh-logout-cooldown.container.model";

@Component({
	selector: "obg-m-zhh-logout-cooldown-container",
	templateUrl: "./zhh-logout-cooldown.container.html",
	styleUrls: ["./zhh-logout-cooldown.container.scss"],
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.None,
})
export class ZhhLogoutCooldownContainer implements OnInit, OnDestroy {
	@HostBinding("class.obg-m-zhh-logout-cooldown") hostClass = true;
	@ViewChild("formDirective") formDirective: FormGroupDirective | undefined;

	logoutCooldownFormGroup: UntypedFormGroup;
	activeLogoutCooldownLimit: LogoutCooldownLimit | undefined;
	queuedLogoutCooldownLimit: LogoutCooldownLimit | undefined;
	inputInformation: string | undefined;
	activeCooldownInHours: number | undefined;
	queuedCooldownInHours: number | undefined;

	placeholder = "logout-cooldown.placeholder";
	isInfoVisible = false;
	isLogoutCooldownLimitInitialized = false;
	isLimitUpdateLoading = false;
	isLimitsHistoryEmpty = false;
	ZHH_LOGOUT_COOLDOWN_MIN_VALUE = 1;
	ZHH_LOGOUT_COOLDOWN_MAX_VALUE = 23;

	private data$$: Subscription | undefined;
	private snackbars$$: Subscription[] = [];

	constructor(
		private formBuilder: UntypedFormBuilder,
		private alertService: AbstractTranslatableAlertService,
		private responsibleGamingFormService: ResponsibleGamingFormService,
		private changeDetectorRef: ChangeDetectorRef,
		private store: Store<CommonState>,
		private logoutCooldownSelector: LogoutCooldownLimitSelector,
		private messageService: MessageService,
		private snackBarService: AbstractTranslatableSnackBarService,
		private logoutCooldownAction: LogoutCooldownLimitAction
	) {
		this.logoutCooldownFormGroup = this.formBuilder.group({
			limit: this.formBuilder.control("", [
				Validators.required,
				OBG_VALIDATORS.numberBiggerOrEqual(() => this.ZHH_LOGOUT_COOLDOWN_MIN_VALUE),
				OBG_VALIDATORS.numberSmallerOrEqual(() => this.ZHH_LOGOUT_COOLDOWN_MAX_VALUE),
			]),
		});
	}

	ngOnInit() {
		this.resetForm();
		this.store.dispatch(this.logoutCooldownAction.get());

		const resetForm$ = this.responsibleGamingFormService.resetForm$.pipe(
			tap(() => this.logoutCooldownFormGroup?.reset())
		);

		const logoutCooldownLimit$ = this.store.pipe(
			select(this.logoutCooldownSelector.getLogoutCooldownLimit()),
			ignoreNil(),
			tap((logoutCooldownState: LogoutCooldownLimitState) => {
				const activeCoolDownInMinutes = logoutCooldownState.activeLogoutCooldownLimit?.cooldownInMinutes;
				const queuedCoolDownInMinutes = logoutCooldownState.activeLogoutCooldownLimit?.cooldownInMinutes;
				this.isLimitsHistoryEmpty = !!logoutCooldownState.isLimitsHistoryEmpty;
				this.activeLogoutCooldownLimit = logoutCooldownState.activeLogoutCooldownLimit;
				this.activeCooldownInHours = activeCoolDownInMinutes ? activeCoolDownInMinutes / 60 : undefined;
				this.queuedLogoutCooldownLimit = logoutCooldownState.queuedLogoutCooldownLimit;
				this.queuedCooldownInHours = queuedCoolDownInMinutes ? queuedCoolDownInMinutes / 60 : undefined;
				this.inputInformation = this.getInputInformation();

				if (
					!this.isLimitsHistoryEmpty &&
					this.activeLogoutCooldownLimit &&
					!this.activeLogoutCooldownLimit?.cooldownInMinutes
				) {
					this.placeholder = "logout-cooldown.not-set";
				}
			})
		);

		const logoutCooldownValueChanges$ = this.logoutCooldownFormGroup.controls.limit.valueChanges.pipe(
			tap(() => (this.inputInformation = this.getInputInformation()))
		);

		const isFormDirty$ = this.logoutCooldownFormGroup.valueChanges.pipe(
			tap(() => this.responsibleGamingFormService.sendIsFormDirty(this.logoutCooldownFormGroup.dirty))
		);

		this.data$$ = merge(
			resetForm$,
			logoutCooldownLimit$,
			logoutCooldownValueChanges$,
			isFormDirty$,
			this.handleMessages$()
		).subscribe(() => this.changeDetectorRef.markForCheck());
	}

	ngOnDestroy() {
		this.data$$?.unsubscribe();
		this.snackbars$$.map((snackbar$$) => snackbar$$.unsubscribe());
	}

	generateAlertMessageText(submitType: ZhhLogoutCooldownSubmitType): string {
		if (this.activeLogoutCooldownLimit?.cooldownInMinutes) {
			if (
				submitType === "Delete" ||
				this.logoutCooldownFormGroup.controls.limit.value < this.activeCooldownInHours!
			) {
				return "responsible-gaming.increase-limit-text";
			}
			return "responsible-gaming.decrease-limit-text";
		}
		return "logout-cooldown.confirmation-caption";
	}

	submitLogoutCooldown(submitType: ZhhLogoutCooldownSubmitType) {
		this.alertService.open(
			{
				id: "ZHH_LOGOUT_COOLDOWN_ALERT_ID",
				title: "logout-cooldown.confirmation-title",
				message: this.generateAlertMessageText(submitType),
				hideClose: true,
				dialogConfig: {
					disableClose: true,
				},
				mustBeInteracted: true,
				buttons: [
					{
						text: "general.cancel",
						color: AlertButtonColor.Primary,
					},
					{
						text: "general.confirm",
						handler: () => {
							switch (submitType) {
								case "Set":
									this.dispatchUpdate(this.logoutCooldownFormGroup?.controls.limit?.value * 60);
									break;
								case "Undo":
									if (this.queuedLogoutCooldownLimit) {
										this.dispatchDelete(this.queuedLogoutCooldownLimit?.limitId);
									}
									if (
										!this.queuedLogoutCooldownLimit &&
										this.activeLogoutCooldownLimit?.cooldownInMinutes
									) {
										this.dispatchUpdate(this.activeLogoutCooldownLimit.cooldownInMinutes);
									}
									break;
								case "Delete":
									this.dispatchDelete(this.activeLogoutCooldownLimit?.limitId);
									break;
							}
							this.isLimitUpdateLoading = true;
							this.changeDetectorRef.markForCheck();
						},
					},
				],
			},
			[TranslationGroup.Common]
		);
	}

	openSnackBar(message: Message) {
		const snackbar$$ = this.snackBarService
			.openSnackBar$({
				message: `${TRANSLATION_PREFIX}.${message.messageCode}`,
				action: "general.close",
				duration: 4000,
				type: message.type,
			})
			.pipe(take(1))
			.subscribe();
		this.snackbars$$.push(snackbar$$);
	}

	resetForm() {
		this.formDirective?.resetForm();
	}

	handleMessages$() {
		const getContextHandler$ = this.messageService
			.consume$({
				context: [LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.get],
			})
			.pipe(
				tap((messages) => {
					const message = messages[messages.length - 1];
					this.isLogoutCooldownLimitInitialized = true;
					if (message.type === MessageType.Error) {
						this.openSnackBar(message);
					}
				})
			);

		const updateContextHandler$ = this.consumeMessage(LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.update);
		const deleteContextHandler$ = this.consumeMessage(LOGOUT_COOLDOWN_LIMIT_MESSAGE_CONTEXT.delete);

		return merge(getContextHandler$, updateContextHandler$, deleteContextHandler$);
	}

	toggleInfoVisibility(): void {
		this.isInfoVisible = !this.isInfoVisible;
	}

	dispatchUpdate(cooldownInMinutes: number | null) {
		this.store.dispatch(this.logoutCooldownAction.update({ cooldownInMinutes }));
	}

	dispatchDelete(limitId: string | undefined) {
		if (limitId) {
			this.store.dispatch(this.logoutCooldownAction.delete({ limitId }));
		}
	}

	private consumeMessage(context: string) {
		return this.messageService
			.consume$({
				context: [context],
			})
			.pipe(
				tap((messages) => {
					const message = messages[messages.length - 1];
					this.isLimitUpdateLoading = false;
					if (message.type === MessageType.Success) {
						this.openSnackBar(message);
						this.resetForm();
					} else if (message.type === MessageType.Error) {
						this.openSnackBar(message);
					}
				})
			);
	}

	private getInputInformation(): string {
		if (this.queuedLogoutCooldownLimit?.cooldownInMinutes) {
			return "logout-cooldown.lowering";
		}
		if (!this.queuedLogoutCooldownLimit && this.activeLogoutCooldownLimit?.endDate) {
			return "logout-cooldown.removing-limit";
		}
		return "";
	}
}
